import React from "react";
import PropTypes from "prop-types";
import GTMTracker from "../../utils/tracking/GTMTracker";
import { DEFAULT_SCHEDULE_CHANNEL_ID } from "../../constants/Schedule";
import Navigation from "../../utils/Navigation";
import { getLiveStreamUrl } from "../../utils/Route";

const LIVE_BAR_ANIMATION_STYLE_ID = "live-bar-animation-style";
const ANIMATION_ITERATION_EVENT_NAME = "animationiteration";

class LiveMarquee extends React.Component {
    constructor(...args) {
        super(...args);
        this._updateAnimation = this._updateAnimation.bind(this);
        this._handleLivePageClick = this._handleLivePageClick.bind(this);
        this._animationIterationHandler =
            this._animationIterationHandler.bind(this);
        this._previousLeftOffsetPercent = 0;
        this._animationCount = 0;
    }

    render() {
        return (
            <div className="live-marquee">
                <a
                    href={this._getLiveHref()}
                    className="live-marquee__live-page-link"
                    onClick={this._handleLivePageClick}
                >
                    <div className="live-marquee__live-on-tv">
                        <span>{this.props.localizer("LIVE_ON_BLOOMBERG")}</span>
                    </div>
                    <div
                        className="live-marquee__info"
                        ref={(element) => (this._marqueeViewport = element)}
                    >
                        <div
                            ref={(element) =>
                                (this._marqueePositionContainer = element)
                            }
                            className="live-marquee__position-container"
                        >
                            <div
                                ref={(element) =>
                                    (this._marqueeContentContainer = element)
                                }
                                className="live-marquee__content"
                            >
                                <span className="live-marquee__show-name">
                                    {this.props.showTitle}
                                </span>
                                <span className="live-marquee__guests-text">
                                    {this._getGuestsNames()}
                                </span>
                            </div>
                        </div>
                    </div>
                </a>
            </div>
        );
    }

    componentDidMount() {
        this._marqueePositionContainer.addEventListener(
            ANIMATION_ITERATION_EVENT_NAME,
            this._animationIterationHandler
        );

        this._updateAnimation();
        // [LC] some browsers only return correct dimension after window.load event
        window.addEventListener("load", this._updateAnimation);
        window.addEventListener("resize", this._updateAnimation);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this._updateAnimation);
        window.removeEventListener("load", this._updateAnimation);
    }

    _animationIterationHandler() {
        const positionContainerEl = this._marqueePositionContainer;

        this._animationCount++;
        if (this._animationCount >= 2) {
            positionContainerEl.removeEventListener(
                ANIMATION_ITERATION_EVENT_NAME,
                this._animationIterationHandler
            );
            positionContainerEl.style.animationPlayState = "paused";

            positionContainerEl.addEventListener("mouseenter", () => {
                positionContainerEl.style.animationPlayState = "running";
            });
            positionContainerEl.addEventListener("mouseleave", () => {
                positionContainerEl.style.animationPlayState = "paused";
            });
        }
    }

    _createUpdateAnimationStyle(animationName, endPercent) {
        let styleEl = document.getElementById(LIVE_BAR_ANIMATION_STYLE_ID);

        if (!styleEl) {
            styleEl = document.createElement("style");
            styleEl.setAttribute("id", LIVE_BAR_ANIMATION_STYLE_ID);
            document.head.appendChild(styleEl);
        }

        styleEl.innerHTML = `
            @keyframes ${animationName} {
                0% { left: 0% }
                20% { left: 0% }
                100% { left: ${endPercent}% }
            }
        `;
    }

    _handleLivePageClick(event) {
        this._navigateToLivePage(event);
    }

    _navigateToLivePage(event) {
        GTMTracker.trackEngagement({
            bvpEventAction: "click",
            bvpEventLabel: `${this.props.eventPrefix}.live_on_bloomberg_tv`,
        });

        Navigation.delayNavigateTo(event);
    }

    _getLiveHref() {
        return getLiveStreamUrl(this.props.liveChannelId);
    }

    _getGuestsNames() {
        const { guests } = this.props;
        if (!(guests && guests.length > 0)) {
            return "";
        }

        const guestList = guests.map((guest) => guest.guestName).join(", ");
        return `${this.props.localizer("GUESTS")}: ${guestList}`;
    }

    _updateAnimation() {
        if (this._isContainerViewportInvisible()) {
            return;
        }

        if (this._isContentWiderThanViewportContainer()) {
            const contentWidth = this._marqueeContentContainer.offsetWidth;
            const viewportContainerWidth = this._marqueeViewport.offsetWidth;
            const targetLeftOffsetPercent =
                ((viewportContainerWidth - contentWidth) * 100) /
                    viewportContainerWidth -
                2;

            if (
                this._previousLeftOffsetPercent.toPrecision(2) ===
                targetLeftOffsetPercent.toPrecision(2)
            ) {
                return;
            }
            this._previousLeftOffsetPercent = targetLeftOffsetPercent;

            const duration = Math.max(
                3,
                (contentWidth - viewportContainerWidth) / 60
            );
            // [LC] create new animation name every time to walk around the Safari caching issue
            const animationName = `live-bar-${new Date().getTime()}`;
            this._createUpdateAnimationStyle(
                animationName,
                targetLeftOffsetPercent
            );

            const positionContainerElStyle =
                this._marqueePositionContainer.style;
            positionContainerElStyle.animationName = animationName;
            positionContainerElStyle.animationDuration = `${duration}s`;
        } else {
            this._marqueePositionContainer.style.animation = null;
        }
    }

    _isContainerViewportInvisible() {
        return this._marqueeViewport.offsetWidth <= 0;
    }

    _isContentWiderThanViewportContainer() {
        return (
            this._marqueeContentContainer.offsetWidth >
            this._marqueeViewport.offsetWidth
        );
    }
}

LiveMarquee.defaultProps = {
    eventPrefix: "",
    liveChannelId: DEFAULT_SCHEDULE_CHANNEL_ID,
    showTitle: "",
    guests: [],
    localizer: (input) => input,
};

LiveMarquee.propTypes = {
    eventPrefix: PropTypes.string,
    liveChannelId: PropTypes.string,
    showTitle: PropTypes.string,
    guests: PropTypes.arrayOf(PropTypes.object),
    localizer: PropTypes.func,
};

export default LiveMarquee;
